import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { defaultBillingDayOfWeek, repeatableManualBillingDayOfMonth } from '~/constants';
import { Accounting, BillingTriggerType } from '~/types/api.model';
import { OneTimeInStagesModel, serviceTemplateBundleModel, serviceTemplateModel } from '..';
import { BillingDayOfMonthModel } from '../BillingDayOfMonth';
import { RecurrencePeriodTypes } from '../service-billing/model';
import * as serviceCostModel from '../service-cost';
import { ServiceIntegrationsModel } from '../ServiceIntegrations';
import { makeServiceTemplate, makeBillingOnApproval, makeBillingOnDemand, makeBillingOneTimeManual, makeBillingOngoing, makeBillingRepeatableManual, generateId, makeBillingOneTimeInStages } from './makers';
import { ServiceTemplateType } from './model';
export function billingFromDTO(billedUpon) {
    switch(billedUpon.type){
        case BillingTriggerType.OnApproval:
            return makeBillingOnApproval();
        case BillingTriggerType.OneTimeManual:
            return makeBillingOneTimeManual();
        case BillingTriggerType.Ongoing:
            return makeBillingOngoing(billedUpon.ongoing.recurrence.period.type);
        case BillingTriggerType.RepeatableManual:
            return makeBillingRepeatableManual(billedUpon.repeatableManual.recurrence.period.type);
        case BillingTriggerType.OneTimeInStages:
            return makeBillingOneTimeInStages(OneTimeInStagesModel.fromDTO(billedUpon.oneTimeInStages));
        case BillingTriggerType.OnDemand:
            return makeBillingOnDemand(billedUpon.onDemand.preapprovalPeriod);
    }
}
export function billingToDTO(billing) {
    let _options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    switch(billing.trigger){
        case BillingTriggerType.OnApproval:
            return {
                type: BillingTriggerType.OnApproval
            };
        case BillingTriggerType.OneTimeManual:
            return {
                type: BillingTriggerType.OneTimeManual
            };
        case BillingTriggerType.Ongoing:
            return {
                type: BillingTriggerType.Ongoing,
                ongoing: {
                    isPaused: false,
                    prorateFirstInvoice: false,
                    billUpfront: false,
                    maxCharges: {
                        enabled: false,
                        limit: null,
                        chargesLeft: null
                    },
                    recurrence: {
                        period: {
                            type: billing.recurrencePeriod,
                            monthly: billing.recurrencePeriod === RecurrencePeriodTypes.Monthly ? {
                                dayOfMonth: BillingDayOfMonthModel.make().toDBType()
                            } : null,
                            weekly: billing.recurrencePeriod === RecurrencePeriodTypes.Weekly ? {
                                day: defaultBillingDayOfWeek
                            } : null,
                            biweekly: billing.recurrencePeriod === RecurrencePeriodTypes.BiWeekly ? {
                                day: defaultBillingDayOfWeek
                            } : null,
                            yearly: billing.recurrencePeriod === RecurrencePeriodTypes.Yearly ? {
                                dayOfYear: null
                            } : null
                        }
                    }
                }
            };
        case BillingTriggerType.RepeatableManual:
            return {
                type: BillingTriggerType.RepeatableManual,
                repeatableManual: {
                    billUpfront: true,
                    maxCharges: {
                        enabled: false,
                        limit: null,
                        chargesLeft: null
                    },
                    recurrence: {
                        period: {
                            type: billing.recurrencePeriod,
                            monthly: billing.recurrencePeriod === RecurrencePeriodTypes.Monthly ? {
                                dayOfMonth: BillingDayOfMonthModel.make(repeatableManualBillingDayOfMonth).toDBType()
                            } : null,
                            weekly: billing.recurrencePeriod === RecurrencePeriodTypes.Weekly ? {
                                day: defaultBillingDayOfWeek
                            } : null,
                            biweekly: billing.recurrencePeriod === RecurrencePeriodTypes.BiWeekly ? {
                                day: defaultBillingDayOfWeek
                            } : null,
                            yearly: billing.recurrencePeriod === RecurrencePeriodTypes.Yearly ? {
                                dayOfYear: null
                            } : null
                        }
                    }
                }
            };
        case BillingTriggerType.OneTimeInStages:
            return {
                type: BillingTriggerType.OneTimeInStages,
                oneTimeInStages: billing.oneTimeInStages.toDTO()
            };
        case BillingTriggerType.OnDemand:
            return {
                type: BillingTriggerType.OnDemand,
                onDemand: {
                    preapprovalPeriod: billing.preapprovalPeriod,
                    maxCharges: {
                        enabled: false,
                        limit: null,
                        chargesLeft: null
                    }
                }
            };
    }
}
export function fromService(service, oldServiceTemplate) {
    const { billing } = service;
    function makeBilling() {
        switch(billing.trigger){
            case BillingTriggerType.OnApproval:
                return makeBillingOnApproval();
            case BillingTriggerType.OneTimeManual:
                return makeBillingOneTimeManual();
            case BillingTriggerType.Ongoing:
                return makeBillingOngoing(billing.recurrencePeriod);
            case BillingTriggerType.RepeatableManual:
                return makeBillingRepeatableManual(billing.recurrencePeriod);
            case BillingTriggerType.OneTimeInStages:
                return makeBillingOneTimeInStages(billing.oneTimeInStages);
            case BillingTriggerType.OnDemand:
                return makeBillingOnDemand(billing.preapprovalPeriod);
        }
    }
    return makeServiceTemplate(_object_spread_props(_object_spread({}, oldServiceTemplate), {
        id: service.serviceTemplateId,
        name: service.name,
        description: service.description,
        cost: service.cost,
        integrations: service.integrations,
        billing: makeBilling(),
        bundle: service.bundle ? serviceTemplateBundleModel.fromService(service) : undefined
    }));
}
export function fromDTO(dto) {
    return {
        id: dto.id,
        name: dto.name,
        description: dto.description,
        status: dto.status,
        cost: serviceCostModel.fromDBDTO(dto.cost),
        billing: billingFromDTO(dto.billedUpon),
        origin: dto.origin || null,
        integrations: ServiceIntegrationsModel.fromDTO(dto.integrations),
        usedIn: dto.usedIn || [],
        bundle: dto.bundle ? serviceTemplateBundleModel.fromDTO(dto.bundle) : undefined
    };
}
export function toDTO(service) {
    let { now } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    const dto = _object_spread({
        id: service.id || generateId(),
        name: service.name,
        description: service.description,
        status: service.status,
        cost: serviceCostModel.toDBDTO(service.cost),
        billedUpon: billingToDTO(service.billing, {
            now
        }),
        usedIn: service.usedIn
    }, service.bundle ? {
        type: ServiceTemplateType.Bundle,
        bundle: serviceTemplateBundleModel.toDTO(service.bundle)
    } : {
        type: ServiceTemplateType.Service
    });
    const integratedId = serviceTemplateModel.integratedId({
        serviceTemplate: service,
        accounting: Accounting.QBO
    });
    if (integratedId) {
        dto.integratedId = integratedId;
    }
    if (service.origin) {
        dto.origin = service.origin;
    }
    if (service.integrations.hasIntegrations()) {
        dto.integrations = service.integrations.toJSON();
    }
    return dto;
}
